import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["reveal", "conceal", "panel", "input", "chevron"]
  static values = {
    showWhen: String,
    invert: { type: Boolean, default: false },
    manual: { type: Boolean, default: false }
  }

  connect () {
    if (this.manualValue) {
      const renderedOpen = this.panels.some((element) => !element.hidden)
      const hasErrors = this.panels.some((element) => element.querySelector(".field_with_errors"))
      this.setOpen(renderedOpen || hasErrors)
      this.syncManualFields()
      return
    }

    this.toggle()
  }

  toggle () {
    if (this.manualValue) {
      if (this.checkboxChecked) this.setOpen(true)
      this.syncManualFields()
      return
    }

    const source = this.hasInputTarget
      ? this.inputTarget
      : this.element.querySelector("input[type=radio]:checked")
    let show = source?.type === "checkbox" ? source.checked : this.shownValues.includes(source?.value)
    if (source?.type === "checkbox" && this.invertValue) show = !show

    this.revealTargets.forEach((element) => {
      element.hidden = !show
      if (source?.type !== "checkbox") return

      this.setFieldsDisabled(element, !show)
    })

    // A conceal target shows the other branch: fields for the unchecked state.
    this.concealTargets.forEach((element) => {
      element.hidden = show
      if (source?.type !== "checkbox") return

      this.setFieldsDisabled(element, show)
    })

    this.updateChevrons(show)
  }

  // `showWhen` may list several values, space-separated (the contact statuses that take a note).
  get shownValues () {
    return this.showWhenValue.split(" ")
  }

  togglePanel (event) {
    event.preventDefault()

    const open = this.panels.some((element) => element.hidden)
    this.setOpen(open)
    this.syncManualFields()
  }

  // Bound to `invalid` in the capture phase: the browser can only focus a
  // required field once its folded panel is shown.
  open () {
    this.setOpen(true)
    this.syncManualFields()
  }

  setOpen (open) {
    this.panels.forEach((element) => {
      element.hidden = !open
    })
    this.updateChevrons(open)
  }

  // A checked feature submits its fields even while folded.
  syncManualFields () {
    this.panels.forEach((element) => {
      const enabled = !element.hidden || this.checkboxChecked
      element.querySelectorAll("input, select, textarea").forEach((field) => {
        if (this.skipsField(field)) return

        const nestedReveal = field.closest("[data-form--reveal-target='reveal']")
        if (nestedReveal && nestedReveal !== element && nestedReveal.hidden) {
          this.setFieldDisabled(field, true)
          return
        }

        this.setFieldDisabled(field, !enabled)
      })
    })
  }

  // A collapsed feature panel disables its fields. Opening it must also unlock
  // widgets that copied `disabled` at init (Tom Select, Flatpickr's alt input).
  setFieldsDisabled (element, disabled) {
    element.querySelectorAll("input, select, textarea").forEach((field) => {
      if (this.skipsField(field)) return

      this.setFieldDisabled(field, disabled)
    })
  }

  // Hidden inputs (a checkbox's unchecked value) are always sent. Flatpickr
  // turns its own input hidden, but that one holds the date: it may have been
  // disabled before Flatpickr started, so it follows the panel too.
  skipsField (field) {
    return field.type === "hidden" && !field._flatpickr
  }

  setFieldDisabled (field, disabled) {
    field.disabled = disabled

    if (field.tomselect) {
      if (disabled) field.tomselect.disable()
      else field.tomselect.enable()
    }

    const altInput = field._flatpickr?.altInput
    if (altInput) altInput.disabled = disabled
  }

  updateChevrons (open) {
    if (!this.hasChevronTarget) return

    this.chevronTargets.forEach((icon) => {
      // Admin forms use Font Awesome chevrons, the other layouts Phosphor carets.
      const [up, down] = icon.classList.contains("ph") ? ["ph-caret-up", "ph-caret-down"] : ["fa-chevron-up", "fa-chevron-down"]
      icon.classList.toggle(up, open)
      icon.classList.toggle(down, !open)
    })
  }

  get panels () {
    if (this.hasPanelTarget) return this.panelTargets

    return this.revealTargets
  }

  get checkboxChecked () {
    const checkbox = this.inputTargets.find((input) => input.type === "checkbox")
    return Boolean(checkbox?.checked)
  }
}
